import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ChangeHistoryDrawer } from "../ChangeHistoryDrawer";
import {
    DemandPlanningLoadingKeys,
    getSkuAllocationChangeLog,
    rollbackSkuAllocationChanges,
} from "../../slices/demandPlanningSlice";

const FIELD_LABELS = {
    override_pct: "Override %",
    new_product_override_pct: "New Product Override %",
    approved: "Approved",
    approved_notes: "Approved Notes",
};

const SOURCE_LABELS = {
    manual: "Manual edit",
    bulk_reset: "Bulk reset",
    ai_rebalance: "AI rebalance",
    rollback: "Rollback",
    kip: "Kip (approved)",
};

const SOURCE_TONES = {
    manual: "bg-slate-100 text-slate-700",
    bulk_reset: "bg-amber-50 text-amber-700",
    ai_rebalance: "bg-indigo-50 text-indigo-700",
    rollback: "bg-emerald-50 text-emerald-700",
    kip: "bg-sky-50 text-sky-700",
};

const PERCENT_FIELDS = new Set(["override_pct", "new_product_override_pct"]);
const DEFAULT_PAGE_SIZE = 100;

const formatLogValue = (field, value) => {
    if (value == null || value === "") return "—";
    if (PERCENT_FIELDS.has(field)) {
        const num = Number(value);
        return Number.isFinite(num) ? `${num}%` : String(value);
    }
    if (field === "approved") {
        return String(value).toLowerCase() === "true" ? "Approved" : "Not Approved";
    }
    return String(value);
};

export const SkuAllocationHistoryDrawer = ({
    isOpen,
    onClose,
    demandPlanningId,
    demandPlanningYear,
    onRolledBack,
    marginFromSidebar,
}) => {
    const dispatch = useDispatch();
    const { skuAllocationChangeLog, skuAllocationChangeLogPagination, loadingKeys } = useSelector(
        (state) => state.demandPlanning
    );
    const [pendingRollback, setPendingRollback] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const loading = loadingKeys.includes(
        DemandPlanningLoadingKeys.getSkuAllocationChangeLog
    );
    const rollingBack = loadingKeys.includes(
        DemandPlanningLoadingKeys.rollbackSkuAllocationChanges
    );

    const refreshLog = useCallback(
        (page = 1) => {
            if (!demandPlanningId || demandPlanningYear == null) return;
            dispatch(
                getSkuAllocationChangeLog({
                    demandPlanningId,
                    demandPlanningYear,
                    page,
                    limit: DEFAULT_PAGE_SIZE,
                })
            );
        },
        [demandPlanningId, demandPlanningYear, dispatch]
    );

    useEffect(() => {
        setCurrentPage(1);
        setSearchTerm("");
    }, [demandPlanningId, demandPlanningYear]);

    useEffect(() => {
        if (!isOpen) {
            setCurrentPage(1);
            setSearchTerm("");
            return;
        }
        refreshLog(currentPage);
    }, [isOpen, currentPage, refreshLog]);

    const pagination = skuAllocationChangeLogPagination || {};
    const totalPages = pagination.totalPages || 1;
    const isTheLastPage = pagination.isTheLastPage ?? currentPage >= totalPages;

    const items = useMemo(() => {
        const list = Array.isArray(skuAllocationChangeLog) ? skuAllocationChangeLog : [];
        const term = searchTerm.trim().toLowerCase();
        if (!term) return list;
        return list.filter((item) =>
            String(item.sku || item.productId || "").toLowerCase().includes(term)
        );
    }, [searchTerm, skuAllocationChangeLog]);

    const batchSizes = useMemo(() => {
        const sizes = new Map();
        for (const item of Array.isArray(skuAllocationChangeLog) ? skuAllocationChangeLog : []) {
            sizes.set(item.batchId, (sizes.get(item.batchId) || 0) + 1);
        }
        return sizes;
    }, [skuAllocationChangeLog]);

    const rows = useMemo(() => items.map((item) => {
        const batchSize = batchSizes.get(item.batchId) || 1;
        const fieldLabel = FIELD_LABELS[item.field] || item.field;
        const oldValue = formatLogValue(item.field, item.oldValue);
        const actions = [{
            key: "revert",
            label: "Revert",
            disabled: rollingBack,
            onClick: () => setPendingRollback({
                kind: "change",
                changeLogId: item.id,
                label: `Revert ${item.sku || item.productId} ${fieldLabel} back to ${oldValue}?`,
            }),
        }];
        if (batchSize > 1) {
            actions.push({
                key: "revert-batch",
                label: `Revert batch (${batchSize})`,
                disabled: rollingBack,
                onClick: () => setPendingRollback({
                    kind: "batch",
                    batchId: item.batchId,
                    label: `Revert this entire batch of ${batchSize} changes?`,
                }),
            });
        }
        return {
            id: item.id,
            when: item.changedAt,
            subject: item.sku || item.productId,
            fields: [fieldLabel],
            changes: [{
                key: item.field,
                oldValue,
                newValue: formatLogValue(item.field, item.newValue),
            }],
            source: {
                label: SOURCE_LABELS[item.source] || item.source,
                tone: SOURCE_TONES[item.source] || SOURCE_TONES.manual,
            },
            by: item.changedBy,
            actions,
        };
    }), [batchSizes, items, rollingBack]);

    const submitRollback = useCallback(async () => {
        if (!pendingRollback) return;
        try {
            const result = await dispatch(
                rollbackSkuAllocationChanges(
                    pendingRollback.kind === "batch"
                        ? { batchId: pendingRollback.batchId }
                        : { changeLogId: pendingRollback.changeLogId }
                )
            ).unwrap();
            toast.success(
                `Rolled back ${result?.rolledBackCount ?? 0} change${(result?.rolledBackCount ?? 0) === 1 ? "" : "s"}.`,
                { position: "bottom-right" }
            );
            setPendingRollback(null);
            refreshLog(currentPage);
            if (onRolledBack) await onRolledBack();
        } catch (error) {
            toast.error(
                typeof error === "string" ? error : "Could not roll back changes.",
                { position: "bottom-right" }
            );
        }
    }, [currentPage, dispatch, onRolledBack, pendingRollback, refreshLog]);

    return (
        <ChangeHistoryDrawer
            isOpen={isOpen}
            onClose={onClose}
            title="SKU Allocation Change History"
            description={"Every change to Override %, New Product Override %, and approval fields is logged here. Revert a single change, or revert an entire batch (bulk resets, AI rebalances) in one click."}
            width="800px"
            marginFromSidebar={marginFromSidebar}
            searchTerm={searchTerm}
            onSearchTermChange={(value) => {
                setSearchTerm(value);
                setCurrentPage(1);
            }}
            searchPlaceholder="Filter by SKU…"
            currentPage={currentPage}
            totalPages={totalPages}
            isTheLastPage={isTheLastPage}
            onPageChange={setCurrentPage}
            loading={loading}
            emptyText="No changes logged yet for this plan."
            rows={rows}
            pendingAction={pendingRollback}
            onCancelAction={() => setPendingRollback(null)}
            onConfirmAction={submitRollback}
            actionInFlight={rollingBack}
        />
    );
};

export default SkuAllocationHistoryDrawer;
